import * as THREE from 'three';
import type { Builder } from '../game/building';
import { setInterior, type Opening } from './interior';

// Base interior view (spec section 8, extended) -- the OLOS / LOS model (see ./interior).
//
// THIRD PERSON, inside an enclosed room: the surround goes near-black (OLOS) so a zoomed-out camera can't
// scout threats over the walls -- EXCEPT a generous cone through every nearby opening (open door /
// window), where the real world renders normally (LOS). The cones are anchored at the player's head
// and tracked every frame, so they sweep as you move and fade at their edges. The sky/fog go black,
// loot and grass are hidden, and ceilings above the player are cut away so the camera sees into the
// room. The ground, trees and rocks darken IN-SHADER (so the LOS cones can reveal them).
//
// FIRST PERSON: no occlusion at all. You only see where you look, so the full world renders with no
// tricks -- no darken, no hidden props, no ceiling cutaway.

const DARK = new THREE.Color(0x05070b);
const OPENING_RANGE = 26; // how far an opening can be and still cast a LOS cone (~ the home radius)

function sm01(t: number): number { const c = t < 0 ? 0 : t > 1 ? 1 : t; return c * c * (3 - 2 * c); }

export class Occlusion {
  private factor = 0; // 0 outside .. 1 fully inside
  // Scratch copies of the LIVE day/night fog + background colour, refreshed each frame. Occlusion darkens
  // THESE toward DARK by the enclosure factor; it must NOT capture a colour at init (that froze the fog at
  // the DAYtime colour, so outside a base -- factor 0 -- it forced the fog back to day every frame and the
  // distance lifted to a pale/white band all night). Reading the live colour keeps night fog dark outside.
  private readonly fogBase = new THREE.Color();
  private readonly bgBase = new THREE.Color();
  // Per-opening eased GAZE gains, keyed by quantized opening position. Openings you face reveal
  // fully; behind the view they reveal nothing; the ease makes reveals FADE IN as you turn, so
  // third person mimics the first-person frustum instead of showing every opening at once.
  private readonly gazeGain = new Map<string, number>();

  constructor(
    private readonly scene: THREE.Scene,
    private readonly builder: Builder,
    private readonly hideables: THREE.Object3D[],
  ) {}

  update(px: number, pz: number, feetY: number, firstPerson: boolean, dt: number, lookX: number, lookZ: number, playerYaw: number): void {
    // INCREMENTAL enclosure (owner spec): the dim SCALES with how enclosed the room actually is --
    // roof fraction x wall fraction from the exact room graph. A 2x2 walled room with one ceiling
    // tile dims ~25%; fully sealed dims fully; a roof with no walls (or open ground) never dims the
    // landscape. First person always renders the full world (you only see where you look).
    const enclosure = firstPerson ? 0 : this.builder.enclosureAt(px, pz, feetY);
    this.factor += (enclosure - this.factor) * Math.min(1, dt * 6);
    const f = this.factor;

    // Black out the distant fog and the sky background TOWARD the enclosure; darken the LIVE day/night
    // colour (day-night set it earlier this frame) so outside a base (f=0) the fog stays exactly the current
    // day/night colour -- dark at night -- instead of being forced back to a stale daytime colour.
    const fog = this.scene.fog as THREE.Fog;
    fog.color.lerpColors(this.fogBase.copy(fog.color), DARK, f);
    if (this.scene.background instanceof THREE.Color) this.scene.background.lerpColors(this.bgBase.copy(this.scene.background), DARK, f);
    const openings: Opening[] = enclosure > 0.02 || f > 0.02 ? this.builder.openings(px, pz, OPENING_RANGE) : [];
    // HYBRID GAZE GATE (owner spec): an opening reveals only when the CHARACTER plausibly faces it
    // (generous ~150deg forward arc, full inside ~130deg -- you cannot see out a window at your
    // back) AND it does not sit behind the CAMERA either (very wide ~250deg gate, so orbiting to
    // face your character head-on never reveals the world behind your own view). The two gates
    // MULTIPLY, and each opening's gain eases (~quarter second) so reveals FADE in/out as either
    // the character or the camera turns -- third person mimicking a first-person frustum.
    const ease = Math.min(1, dt * 5);
    const pfx = -Math.sin(playerYaw), pfz = -Math.cos(playerYaw); // character forward: the body's -Z face (see player.ts pose notes)
    for (const o of openings) {
      const dx = o.x - px, dz = o.z - pz;
      const d = Math.hypot(dx, dz) || 1e-3;
      const ox = dx / d, oz = dz / d;
      const faceP = ox * pfx + oz * pfz;                          // cos(character facing -> opening)
      const gateP = sm01((faceP + 0.26) / 0.68);                  // full by ~65deg off-forward, zero past ~105deg
      const faceC = ox * lookX + oz * lookZ;                      // cos(camera view -> opening)
      const gateC = sm01((faceC + 0.77) / 0.51);                  // only kills openings clearly behind the camera
      const target = gateP * gateC;
      const key = `${Math.round(o.x * 2)},${Math.round(o.z * 2)}`;
      const cur = this.gazeGain.get(key) ?? 0;
      const g = cur + (target - cur) * ease;
      this.gazeGain.set(key, g);
      o.gain = g;
    }
    if (this.gazeGain.size > 64) this.gazeGain.clear(); // roaming between bases: drop stale keys wholesale (cheap + rare)
    setInterior(f, px, pz, openings);

    // Toggle the exterior props once the surround is dark enough to hide the pop.
    const hide = f > 0.5;
    for (const h of this.hideables) h.visible = !hide;
    // Ceiling cutaway is a third-person convenience only; in first person you see the roof normally.
    this.builder.setCutaway(hide && !firstPerson ? feetY : null);
  }
}
